/**
 * Analytics event emitter — framework-free, module-scoped.
 *
 * Why module-scoped dedup state (not per-component):
 *   Dedup must guard across all Bridge Bar components simultaneously.
 *   If each component held its own history the 20ms cross-component guard
 *   would never fire (F-2).
 *
 * Why dedup gates on `event && eventResult`:
 *   Legacy behaviour — `bbShown` carries no `eventResult` and must never
 *   be suppressed; the dedup gate only activates for navigation events that
 *   carry both fields (F-5, AD-3).
 *
 * Why tracker is resolved per call (not stored globally):
 *   `getTracker` is passed per-call from component context so it always
 *   reflects the current config — no global provider needed (F-1, AD-2).
 */

/** Internal narrow type — `window.dataLayer` satisfies this via Array.prototype.push. */
interface Tracker {
  push: (...args: unknown[]) => unknown;
}

/**
 * Extend the Window interface so TypeScript knows `window.dataLayer` may exist
 * without us auto-creating it (AD-2, F-4).
 */
declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/** Module-level dedup Map: JSON key → timestamp of last push (ms). */
const dedupMap = new Map<string, number>();

/**
 * Resolve the push target from the optional `getTracker` factory or fall back
 * to `window.dataLayer`. Returns null if neither is available or the resolved
 * value lacks a `push` function.
 */
function resolveTracker(getTracker?: () => unknown): Tracker | null {
  const raw = getTracker ? getTracker() : window.dataLayer;
  if (
    raw !== null &&
    raw !== undefined &&
    typeof (raw as Tracker).push === "function"
  ) {
    return raw as Tracker;
  }
  return null;
}

/**
 * Prune dedup entries older than 20ms to bound memory growth (AD-3).
 * Called at the start of every emit so stale keys can never block future events.
 */
function pruneStaleEntries(now: number): void {
  for (const [key, ts] of dedupMap.entries()) {
    if (now - ts > 20) {
      dedupMap.delete(key);
    }
  }
}

/**
 * Emit a GA event payload to the resolved tracker.
 *
 * Dedup gate: suppresses re-emission of the same payload within 20ms, but ONLY
 * when both `data.event` and `data.eventResult` are truthy (AC-1.4, F-5).
 * Payloads that lack either field (e.g. bbShown) bypass the gate entirely.
 *
 * Tracker resolution order: `getTracker?.()` → `window.dataLayer` → no-op.
 * A `push` that throws is silently swallowed so analytics never breaks the bar (NFR-2).
 *
 * @param data - GA event payload
 * @param getTracker - optional factory returning a custom tracker (AC-1.2)
 */
export function emit(
  data: Record<string, unknown>,
  getTracker?: () => unknown
): void {
  const now = Date.now();
  pruneStaleEntries(now);

  // Dedup gate — only active when BOTH event and eventResult are present (F-5)
  if (data.event && data.eventResult) {
    const key = JSON.stringify(data);
    const lastSeen = dedupMap.get(key);
    // Boundary is inclusive of 20ms (<=) — NFR-1 says "< 20ms between interactions
    // is a double-click"; we treat exactly 20ms as within the same interaction window.
    if (lastSeen !== undefined && now - lastSeen <= 20) {
      return; // suppressed — within the 20ms window (AC-1.4)
    }
    dedupMap.set(key, now);
  }

  try {
    // Resolve tracker inside the try/catch so a throwing getTracker factory is a
    // silent no-op — a host whose factory throws must not break the bridge bar (NFR-2).
    const tracker = resolveTracker(getTracker);
    if (tracker === null) {
      return; // no push target available — silent no-op (AC-1.3)
    }
    tracker.push(data);
  } catch {
    // NFR-2: a throwing tracker (or factory) must never propagate — analytics must not break the bar
  }
}

/**
 * Clear the module-level dedup Map.
 * Call in `beforeEach` to prevent test state leaks across Vitest cases.
 */
export function resetAnalyticsState(): void {
  dedupMap.clear();
}
